import { Tile } from "@dropbox/dash-component-library";
import { Skeleton } from "@dropbox/dig-components/skeleton";
import { UIIcon } from "@dropbox/dig-icons";
import { ArrowUpLine, Twinkle2Line } from "@dropbox/dig-icons/dist/mjs/assets";
import classNames from "classnames";
import React, { SVGProps, useEffect, useState } from "react";
import styles from "./SuggestionTile.module.css";

import type { TileProps } from "@dropbox/dash-component-library";
import type { BoxSkeletonProps } from "@dropbox/dig-components/skeleton";
import { useMirageAnalyticsContext } from "@mirage/analytics/AnalyticsProvider";
import { PAP_Shown_DashQuerySuggestionPrompt } from "@mirage/analytics/events/types/shown_dash_query_suggestion_prompt";
import useSearchQueryId from "@mirage/shared/hooks/useSearchQueryId";

export type SuggestionTileProps = {
  text: string;
  onClick: React.MouseEventHandler<HTMLButtonElement>;
  isLoading?: boolean;

  /**
   * The size of the tile. Large size will be fixed in height, allowing text to wrap up to max 2 lines, unless `wrap` is set to false.
   * Large size will assume `display` is `block`, while small and medium will assume `display` is `inline`.
   * @default 'large'
   */
  size?: TileProps["size"];

  /**
   * The display style of the tile. `block` will take the full width of the container, `inline` will take the width of the text.
   * @default 'block'
   */
  display?: "block" | "inline";

  /**
   * Whether to allow text to wrap. If set to false, will enforce a single line truncation.
   * @default true
   */
  wrap?: boolean;

  /**
   * The styles to apply to the text of the tile.
   */
  textStyles?: React.CSSProperties;

  /**
   * Styles to apply directly to the Tile component.
   */
  tileStyles?: React.CSSProperties;

  /**
   * Whether to add additional padding to the icon (for ConversationSuggestedQueries variant).
   * @default false
   */
  withPadding?: boolean;

  /**
   * The predictionId of the query suggestion.
   */
  predictionId?: string;

  /**
   * The conversationId of the query suggestion.
   */
  conversationId?: string;

  /**
   * The assistRequestId of the query suggestion.
   */
  assistRequestId?: string;

  /**
   * The searchSessionId of the query suggestion.
   */
  searchSessionId?: string;

  /**
   * Whether to render the tile without a background.
   * @default false
   */
  withNoBackground?: boolean;

  /**
   * The source of the icon to use instead of the default one.
   */
  customIconSrc?: React.ComponentType<SVGProps<SVGSVGElement>>;

  /**
   * Whether the tile is an external link and should have an external link icon.
   */
  isExternalLink?: boolean;
};

export function SuggestionTile({
  text,
  size = "large",
  display,
  wrap = true,
  onClick,
  isLoading,
  textStyles,
  tileStyles,
  withPadding = false,
  predictionId,
  conversationId,
  assistRequestId,
  searchSessionId,
  withNoBackground = false,
  customIconSrc,
  isExternalLink,
}: SuggestionTileProps) {
  const { reportPapEvent } = useMirageAnalyticsContext();
  const { searchQueryUuid } = useSearchQueryId();
  const [hasLoggedExposure, setHasLoggedExposure] = useState(false);

  useEffect(() => {
    if (isLoading || hasLoggedExposure) {
      return;
    }

    reportPapEvent(
      PAP_Shown_DashQuerySuggestionPrompt({
        actionSurfaceComponent: "prompt_suggestion",
        predictionId: predictionId,
        featureLine: "assist",
        conversationId,
        assistRequestId,
        searchSessionId,
        searchQueryUuid,
      }),
    );

    setHasLoggedExposure(true);
  }, [reportPapEvent, hasLoggedExposure, isLoading]);

  if (isLoading) {
    let height: BoxSkeletonProps["height"];
    switch (size) {
      case "large":
        height = 56;
        break;
      case "small":
      case "medium":
      default:
        height = 42;
        break;
    }
    return (
      <Skeleton.Box
        // Approximation of the height of the Tile
        // Could invest in calculating more precisely
        height={height}
        withBorderRadius="xlarge"
        className={classNames(
          styles.skeleton,
          {
            [styles.sizeSmall]: size === "small",
            [styles.sizeMedium]: size === "medium",
            [styles.sizeLarge]: size === "large",
          },
          "meticulous-redact-recording",
        )}
      />
    );
  }

  // If the display prop is set, we respect it
  // Else
  // if the tile size is large, we assume it should behave like display: block,
  //    and take the full width
  // If the tile size is small or medium, we assume it should behave like display: inline,
  //    and take the width of the text, or container as needed
  const isFullWidth = display ? display === "block" : size === "large";

  return (
    <Tile
      className={classNames(styles.suggestionTile, {
        [styles.sizeSmall]: size === "small",
        [styles.sizeMedium]: size === "medium",
        [styles.sizeLarge]: size === "large",
        [styles.withPadding]: withPadding,
        [styles.noBackground]: withNoBackground,
      })}
      onClick={onClick}
      isFullWidth={isFullWidth}
      size={size}
      withLabel={text}
      withLabelProps={{
        style: {
          padding: "var(--dig-spacing__micro__small) 0",
          display: "flex",
          ...textStyles,
        },
        // If `wrap` set to false, enforce one line of text only
        // Else, if size is large, enforce two lines of text
        // Else, allow text to wrap as needed
        maxLines: !wrap ? 1 : size === "large" ? 2 : 999,
      }}
      withStartAccessory={
        <UIIcon
          src={customIconSrc || Twinkle2Line}
          className={classNames(styles.icon, {
            [styles.withPadding]: withPadding,
            [styles.subtleIcon]: withNoBackground,
          })}
        />
      }
      withEndAccessory={
        isExternalLink && (
          <UIIcon
            data-testid="external-link-icon"
            src={ArrowUpLine}
            className={classNames(styles.externalLinkIcon, {
              [styles.withPadding]: withPadding,
            })}
          />
        )
      }
      endAccessoryLayout={isExternalLink ? "inline" : undefined}
      style={tileStyles}
    />
  );
}
